import { useState, useEffect, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import {
  Pulse, Play, ArrowClockwise, CheckCircle, XCircle,
  Bell, Buildings, Clock, Warning, ToggleLeft, ToggleRight,
  ChartBar, Funnel,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/* ─── helpers ────────────────────────────────────────────────── */
function JobRow({ label, value, mono = false }) {
  return (
    <div className="flex justify-between items-center py-1.5 border-b border-white/5 last:border-0">
      <span className="text-xs text-zinc-500">{label}</span>
      <span className={`text-xs ${mono ? "font-mono text-amber-400" : "text-white"}`}>{value ?? "—"}</span>
    </div>
  );
}

function StatCard({ label, value, sub, color = "text-white" }) {
  return (
    <div className="jp-card p-4">
      <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">{label}</div>
      <div className={`text-2xl font-mono font-bold ${color}`}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-600 mt-0.5">{sub}</div>}
    </div>
  );
}

/* ─── Radar Config Editor ─────────────────────────────────────── */
function RadarConfigPanel({ cfg, onSave }) {
  const [form, setForm] = useState({
    enabled:        cfg.enabled,
    interval_hours: cfg.interval_hours ?? 6,
    min_score:      cfg.min_score ?? 75,
    location:       cfg.location ?? "India",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      enabled:        cfg.enabled,
      interval_hours: cfg.interval_hours ?? 6,
      min_score:      cfg.min_score ?? 75,
      location:       cfg.location ?? "India",
    });
  }, [cfg]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="jp-card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Radar Config</h3>
        <button
          onClick={() => set("enabled", !form.enabled)}
          className="flex items-center gap-1.5 text-xs font-medium transition-colors"
        >
          {form.enabled
            ? <ToggleRight size={20} weight="fill" className="text-amber-400" />
            : <ToggleLeft  size={20} weight="fill" className="text-zinc-600"  />
          }
          <span className={form.enabled ? "text-amber-400" : "text-zinc-600"}>
            {form.enabled ? "Enabled" : "Disabled"}
          </span>
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-1">Interval (hours)</label>
          <input
            type="number" min={1} max={72}
            value={form.interval_hours}
            onChange={e => set("interval_hours", Number(e.target.value))}
            className="w-full bg-white/5 border border-white/8 rounded-lg px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-amber-500/50"
          />
        </div>
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-1">Min Match Score</label>
          <input
            type="number" min={50} max={100}
            value={form.min_score}
            onChange={e => set("min_score", Number(e.target.value))}
            className="w-full bg-white/5 border border-white/8 rounded-lg px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-amber-500/50"
          />
        </div>
        <div className="col-span-2">
          <label className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-1">Target Location</label>
          <input
            type="text"
            value={form.location}
            onChange={e => set("location", e.target.value)}
            className="w-full bg-white/5 border border-white/8 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500/50"
          />
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full py-2 rounded-lg text-sm font-semibold bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-50 transition-all"
      >
        {saving ? "Saving…" : "Save Radar Config"}
      </button>
    </div>
  );
}

/* ─── Scheduler Jobs Table ────────────────────────────────────── */
function SchedulerJobs({ jobs, running }) {
  if (!running) {
    return (
      <div className="jp-card p-5 flex items-center gap-3">
        <XCircle size={20} weight="fill" className="text-red-400 shrink-0" />
        <div>
          <p className="text-sm text-red-400 font-medium">Scheduler not running</p>
          <p className="text-xs text-zinc-500 mt-0.5">The APScheduler instance is stopped. Restart the server to re-activate it.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="jp-card overflow-hidden">
      <div className="px-5 py-3 border-b border-white/5 flex items-center gap-2">
        <CheckCircle size={14} weight="fill" className="text-emerald-400" />
        <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Active Cron Jobs ({jobs.length})</span>
      </div>
      {jobs.length === 0 ? (
        <div className="p-5 text-center text-sm text-zinc-600 italic">No scheduled jobs found.</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] text-zinc-500 uppercase tracking-wider border-b border-white/5">
              <th className="text-left px-5 py-2">Job ID</th>
              <th className="text-left px-4 py-2">Trigger</th>
              <th className="text-right px-4 py-2">Next Run</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {jobs.map(job => (
              <tr key={job.id} className="hover:bg-white/[0.02]">
                <td className="px-5 py-3">
                  <span className="text-xs font-mono text-amber-400">{job.id}</span>
                </td>
                <td className="px-4 py-3 text-xs text-zinc-400 max-w-[180px] truncate">{job.trigger}</td>
                <td className="px-4 py-3 text-right text-xs text-zinc-300 font-mono">
                  {job.next_run_time
                    ? new Date(job.next_run_time).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
                    : "—"
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ─── Alert Stats sparkline ──────────────────────────────────── */
function AlertStats({ stats }) {
  if (!stats) return null;

  // Group last 7 days
  const today = new Date();
  const dates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() - (6 - i));
    return d.toISOString().slice(0, 10);
  });

  const byDate = {};
  (stats.by_type_date || []).forEach(r => {
    if (!byDate[r.date]) byDate[r.date] = 0;
    byDate[r.date] += r.count;
  });

  const values = dates.map(d => byDate[d] || 0);
  const maxVal = Math.max(...values, 1);
  const W = 200, H = 40;

  return (
    <div className="jp-card p-5">
      <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-4">Alert Activity (7 days)</h3>
      <div className="flex items-end gap-2 mb-4">
        <StatCard label="Total Alerts" value={stats.total} />
        <StatCard label="Unread" value={stats.unread} color="text-amber-400" />
      </div>
      {/* bar chart */}
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none">
        {values.map((v, i) => {
          const barH = Math.max(2, (v / maxVal) * H);
          const x = (i / 7) * W;
          const bw = W / 7 - 2;
          return (
            <rect
              key={i}
              x={x + 1}
              y={H - barH}
              width={bw}
              height={barH}
              rx={2}
              className="fill-amber-500/60"
            />
          );
        })}
      </svg>
      <div className="flex justify-between mt-1">
        {dates.map(d => (
          <span key={d} className="text-[9px] text-zinc-700">
            {new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─── Watchlist Config Panel ──────────────────────────────────── */
function WatchlistConfigPanel({ cfg, onSave }) {
  const [form, setForm] = useState({
    enabled:        cfg.enabled ?? false,
    interval_hours: cfg.interval_hours ?? 12,
    min_score:      cfg.min_score ?? 70,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    setForm({ enabled: cfg.enabled ?? false, interval_hours: cfg.interval_hours ?? 12, min_score: cfg.min_score ?? 70 });
  }, [cfg]);

  const handleSave = async () => { setSaving(true); await onSave(form); setSaving(false); };

  return (
    <div className="jp-card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Watchlist Scanner</h3>
        <button onClick={() => set('enabled', !form.enabled)} className="flex items-center gap-1.5 text-xs font-medium transition-colors">
          {form.enabled ? <ToggleRight size={20} weight="fill" className="text-sky-400" /> : <ToggleLeft size={20} weight="fill" className="text-zinc-600" />}
          <span className={form.enabled ? 'text-sky-400' : 'text-zinc-600'}>{form.enabled ? 'Enabled' : 'Disabled'}</span>
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-1">Interval (hours)</label>
          <input type="number" min={1} max={168} value={form.interval_hours} onChange={e => set('interval_hours', Number(e.target.value))}
            className="w-full bg-white/5 border border-white/8 rounded-lg px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-sky-500/50" />
        </div>
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-1">Min Match Score</label>
          <input type="number" min={50} max={100} value={form.min_score} onChange={e => set('min_score', Number(e.target.value))}
            className="w-full bg-white/5 border border-white/8 rounded-lg px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-sky-500/50" />
        </div>
      </div>
      <button onClick={handleSave} disabled={saving}
        className="w-full py-2 rounded-lg text-sm font-semibold bg-sky-500 text-black hover:bg-sky-400 disabled:opacity-50 transition-all">
        {saving ? 'Saving…' : 'Save Watchlist Config'}
      </button>
    </div>
  );
}

/* ─── Auto-Apply Config Panel ────────────────────────────────── */
function AutoApplyConfigPanel({ cfg, onSave }) {
  const [form, setForm] = useState({
    enabled:   cfg.enabled ?? false,
    min_score: cfg.min_score ?? 85,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    setForm({ enabled: cfg.enabled ?? false, min_score: cfg.min_score ?? 85 });
  }, [cfg]);

  const handleSave = async () => { setSaving(true); await onSave(form); setSaving(false); };

  return (
    <div className="jp-card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Auto-Apply Assistant</h3>
        <button onClick={() => set('enabled', !form.enabled)} className="flex items-center gap-1.5 text-xs font-medium transition-colors">
          {form.enabled ? <ToggleRight size={20} weight="fill" className="text-emerald-400" /> : <ToggleLeft size={20} weight="fill" className="text-zinc-600" />}
          <span className={form.enabled ? 'text-emerald-400' : 'text-zinc-600'}>{form.enabled ? 'Enabled' : 'Disabled'}</span>
        </button>
      </div>
      <div>
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-1">Min Score for Auto-Apply</label>
        <input type="number" min={50} max={100} value={form.min_score} onChange={e => set('min_score', Number(e.target.value))}
          className="w-full bg-white/5 border border-white/8 rounded-lg px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-emerald-500/50" />
        <p className="text-[10px] text-zinc-600 mt-1">Jobs scoring above this threshold appear in the Auto-Apply queue.</p>
      </div>
      <button onClick={handleSave} disabled={saving}
        className="w-full py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50 transition-all">
        {saving ? 'Saving…' : 'Save Auto-Apply Config'}
      </button>
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────── */
export default function AdminRadarTab() {
  const {
    fetchCronJobs, adminRunRadarNow, adminRunWatchlistNow,
    adminSaveRadarConfig, adminSaveWatchlistConfig, adminSaveAutoApplyConfig, fetchAlertStats,
  } = useApp();

  const [data, setData] = useState(null);
  const [alertStats, setAlertStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningRadar, setRunningRadar] = useState(false);
  const [runningWatchlist, setRunningWatchlist] = useState(false);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    const [cronData, statsData] = await Promise.all([fetchCronJobs(), fetchAlertStats()]);
    setData(cronData);
    setAlertStats(statsData);
    setLoading(false);
  }, [fetchCronJobs, fetchAlertStats]);

  useEffect(() => { load(); }, [load]);

  const handleRunRadar = async () => {
    setRunningRadar(true);
    try {
      await adminRunRadarNow();
      toast.success("Radar cycle triggered — check alerts in ~30s");
    } catch {
      toast.error("Failed to trigger radar");
    } finally {
      setRunningRadar(false);
    }
  };

  const handleRunWatchlist = async () => {
    setRunningWatchlist(true);
    try {
      await adminRunWatchlistNow();
      toast.success("Watchlist scan triggered — check alerts in ~60s");
    } catch {
      toast.error("Failed to trigger watchlist scan");
    } finally {
      setRunningWatchlist(false);
    }
  };

  const handleSaveRadarConfig = async (updates) => {
    try {
      await adminSaveRadarConfig(updates);
      toast.success("Radar config saved");
      await load(true);
    } catch {
      toast.error("Failed to save radar config");
    }
  };

  const handleSaveWatchlistConfig = async (updates) => {
    try {
      await adminSaveWatchlistConfig(updates);
      toast.success("Watchlist config saved");
      await load(true);
    } catch {
      toast.error("Failed to save watchlist config");
    }
  };

  const handleSaveAutoApplyConfig = async (updates) => {
    try {
      await adminSaveAutoApplyConfig(updates);
      toast.success("Auto-Apply config saved");
      await load(true);
    } catch {
      toast.error("Failed to save auto-apply config");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-5 h-5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        <span className="ml-3 text-sm text-zinc-500">Loading cron status…</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-white">Radar & Background Jobs</h2>
          <p className="text-xs text-zinc-500 mt-0.5">Monitor APScheduler, manually trigger cycles, and update radar settings.</p>
        </div>
        <button
          onClick={() => load(true)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-zinc-400 hover:text-white hover:bg-white/5 border border-white/5 hover:border-white/10 transition-all"
        >
          <ArrowClockwise size={15} />
          Refresh
        </button>
      </div>

      {/* Quick-action buttons */}
      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={handleRunRadar}
          disabled={runningRadar}
          className="jp-card p-5 flex items-center gap-4 hover:border-amber-500/30 transition-all disabled:opacity-50 group"
        >
          <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
            <Bell size={20} weight="fill" className="text-amber-400" />
          </div>
          <div className="text-left">
            <div className="text-sm font-semibold text-white group-hover:text-amber-400 transition-colors flex items-center gap-2">
              Run Radar Now
              {runningRadar && <div className="w-3 h-3 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />}
            </div>
            <div className="text-xs text-zinc-500 mt-0.5">Force an immediate Job Radar scan cycle</div>
          </div>
          <Play size={16} className="ml-auto text-zinc-600 group-hover:text-amber-400 transition-colors" weight="fill" />
        </button>

        <button
          onClick={handleRunWatchlist}
          disabled={runningWatchlist}
          className="jp-card p-5 flex items-center gap-4 hover:border-sky-500/30 transition-all disabled:opacity-50 group"
        >
          <div className="w-10 h-10 rounded-lg bg-sky-500/10 border border-sky-500/20 flex items-center justify-center shrink-0">
            <Buildings size={20} weight="fill" className="text-sky-400" />
          </div>
          <div className="text-left">
            <div className="text-sm font-semibold text-white group-hover:text-sky-400 transition-colors flex items-center gap-2">
              Run Watchlist Scan
              {runningWatchlist && <div className="w-3 h-3 border-2 border-sky-400 border-t-transparent rounded-full animate-spin" />}
            </div>
            <div className="text-xs text-zinc-500 mt-0.5">Scrape all active watchlist career pages now</div>
          </div>
          <Play size={16} className="ml-auto text-zinc-600 group-hover:text-sky-400 transition-colors" weight="fill" />
        </button>
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-3 gap-6">
        {/* Left: Radar config + scheduler */}
        <div className="col-span-2 space-y-5">
          {/* Scheduler status */}
          <SchedulerJobs
            jobs={data?.jobs || []}
            running={data?.scheduler_running ?? false}
          />

          {/* Radar config */}
          {data?.radar && (
            <RadarConfigPanel cfg={data.radar} onSave={handleSaveRadarConfig} />
          )}

          {/* Watchlist config — editable */}
          {data?.watchlist && (
            <WatchlistConfigPanel cfg={data.watchlist} onSave={handleSaveWatchlistConfig} />
          )}

          {/* Auto-Apply config — editable */}
          {data?.auto_apply !== undefined && (
            <AutoApplyConfigPanel cfg={data.auto_apply || {}} onSave={handleSaveAutoApplyConfig} />
          )}
        </div>

        {/* Right: Alert stats + personas */}
        <div className="space-y-5">
          <AlertStats stats={alertStats} />

          {/* Personas with resume status */}
          <div className="jp-card p-5">
            <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-3">Personas in Pool</h3>
            {(data?.personas || []).length === 0 ? (
              <p className="text-xs text-zinc-600 italic">No personas found.</p>
            ) : (
              <div className="space-y-2">
                {data.personas.map(p => (
                  <div key={p.id} className="flex items-center gap-2.5">
                    <div className={`w-2 h-2 rounded-full shrink-0 ${p.has_resume ? "bg-emerald-400" : "bg-zinc-700"}`} />
                    <span className="text-xs text-zinc-300 truncate flex-1">{p.name}</span>
                    {!p.has_resume && (
                      <span className="text-[9px] text-zinc-600 uppercase tracking-wider">no resume</span>
                    )}
                  </div>
                ))}
              </div>
            )}
            <p className="text-[10px] text-zinc-700 mt-3 italic">
              Only personas with a resume are used for scoring.
            </p>
          </div>

          {/* Info card */}
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/5 border border-amber-500/10">
            <Warning size={14} weight="fill" className="text-amber-500 mt-0.5 shrink-0" />
            <p className="text-[10px] text-zinc-500">
              "Run Now" fires async background tasks. The server returns immediately — check alerts in the main app to verify results.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
